import React, { useCallback, useEffect, useState } from 'react';
import { SafeAreaView, Platform, StatusBar } from 'react-native';
import { View, Text, Center, FlatList, Spinner, useTheme } from 'native-base';
import HandlePost from '../common/HandlePost';
import Topic from '../topics/Topic';
import { useAuthState, authUtil } from '../../../context/auth';
import { useEnvState, useEnvDispatch } from '../../../context/env';
import { topicsAPI, useTopicsState, useTopicsDispatch } from '../../../context/topics';
import { useCategoriesState } from '../../../context/categories';
import { useTopicsManager } from '../../hooks/useTopicsManager';
import { useMessageBusState } from '../../../context/messagebus';
import { useMessageBus } from '../../hooks/useMessageBus';
import Banner from '../common/reusables/Banner';
import { AVATAR_SIZE } from '../../Constants';
import { clone } from '../../utils/helpers';
const Comments = props => {
  const theme = useTheme(),
    {
      colors
    } = theme;
  const {
    slug,
    path
  } = props;
  const topicsDispatch = useTopicsDispatch();
  const authState = useAuthState();
  const envState = useEnvState();
  const envDispatch = useEnvDispatch();
  const categoriesState = useCategoriesState();
  const {
    getDigestInfo,
    updateTopicPostContents
  } = useTopicsManager();
  const {
    selectedCategory
  } = categoriesState;
  const [comments, setComments] = useState([]);
  const [showSpinner, setShowSpinner] = useState(true);
  const [nextPage, setNextPage] = useState(0);
  const [hasMoreTopics, setHasMoreTopics] = useState(true);

  // manage User Profile (Card) Modal
  const [showUserProfileModal, setShowUserProfileModal] = useState(false);
  const [profileUser, setProfileUser] = useState(false);
  const [reactRefReady, setReactRefReady] = useState(false);
  const {
    latest_topics_users
  } = useTopicsState();
  const updateTopicPost = (id, post) => {
    updateTopicPostContents(id, post, comments, setComments);
  };
  const updateTopicReplies = () => {
    setNextPage(0);
    setHasMoreTopics(true);
  };

  // initial loading of topics
  useEffect(() => {
    if (selectedCategory) {
      !nextPage && getTopicsOfContentItem(true);
    }
  }, [authState.api_key, selectedCategory, nextPage, hasMoreTopics]);

  // TODO: exact duplicate of function from src/components/content/ForumHomeScreen.tsx, so we could probably consolidate
  const updateContextSetTopicUsers = latestUsers => {
    // clone latest_topics_users from Topics context, we will be adding new users to this list
    const usersList = clone(latest_topics_users);

    // build array of IDs from usersList
    const userIds = usersList.map(user => user.id);

    // filter list of latest users removing users found in userIds array
    const filteredNewUsers = latestUsers.filter(user => !userIds.includes(user.id));

    // for each new user found in filtered list, add to usersList
    filteredNewUsers.forEach(user => {
      usersList.push(user);
    });

    // update context with updated usersList
    topicsDispatch({
      type: 'SET_LATEST_TOPICS_USERS',
      payload: usersList
    });
  };
  const getTopicsOfContentItem = async function () {
    let forceReset = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    if (hasMoreTopics) {
      setShowSpinner(true);
      const res = await topicsAPI.getLatestTopicsByTag(envState.base_url, selectedCategory.name, selectedCategory.id, slug, nextPage, authState.api_key);
      if (res && res.topic_list && res.topic_list.topics && res.topic_list.topics.length) {
        setNextPage(currentPage => currentPage + 1); // for pagination
        const {
          topics
        } = res.topic_list;
        // save users in context
        if (res.users) {
          updateContextSetTopicUsers(res.users);
        }
        const digestedTopics = await getDigestInfo(topics);
        // need to ensure uniqueness
        let uniqueTopics = digestedTopics.reduce((accum, current) => {
          let topicsSoFar = accum.map(a => a.id);
          if (topicsSoFar.includes(current.id)) {
            return accum;
          } else {
            return [...accum, current];
          }
        }, forceReset ? [] : comments);
        setComments(uniqueTopics);
        setShowSpinner(false);
      } else {
        setHasMoreTopics(false); // no need for loadMoreTopics api request
        setShowSpinner(false);
        return false;
      }
    } else {
      return false;
    }
  };
  useEffect(() => {
    if (profileUser && props.toggleProfileModal) {
      /* if this is defined, it was passed down via an embeddable,
       * so don't use the component defined in this repo
       * just pass this value back up to the embedder
       */

      props.toggleProfileModal(parseInt(profileUser.username.replace(new RegExp(`${envState.discourse_api_username_prefix}\.`), '')));
    }
  }, [profileUser, showUserProfileModal]);
  const normalizeAvatar = user => {
    const avatarPrefix = user.avatar_template.indexOf('http') !== -1 ? '' : envState === null || envState === void 0 ? void 0 : envState.base_url;
    const avatarTemplate = avatarPrefix + user.avatar_template.replace(/\{size\}/, AVATAR_SIZE);
    user.avatar_template = avatarTemplate;
    return user;
  };
  const showUserProfile = user => {
    setProfileUser(normalizeAvatar(user));
    setShowUserProfileModal(true);
  };

  /**
   * Initialize the MessageBus, the call startPollingForMessages() to establish polling/listening
   *  initialized means the script was found and valid context/state vars exist
   *  startPollingForMessages sets the isListening value to true
   */

  const {
    isInitialized,
    closeReplyBanner,
    refreshTopic,
    refreshTopics,
    startPollingForMessages,
    getUniqueList
  } = useMessageBus({
    userAuthenticated: authUtil.userAuthed(authState)
  });
  const messageBusState = useMessageBusState();
  const [topicIdListForTopics, setTopicIdListForTopics] = useState([]);
  const [topicIdListForReplies, setTopicIdListForReplies] = useState([]);

  // MessageBus refresh actions
  const refreshBannerForTopics = id => {
    refreshTopics(); // MessageBus routine
    updateTopicReplies();
  };
  const refreshBannerForReplies = id => {
    refreshTopic(); // MessageBus routine
    updateTopicReplies();
  };
  useEffect(() => {
    setTopicIdListForReplies(getUniqueList(messageBusState.replies));
  }, [messageBusState.replies]);
  useEffect(() => {
    setTopicIdListForTopics(getUniqueList(messageBusState.topics));
  }, [messageBusState.topics]);

  // once initialized, start the message bus, start polling for new messages
  useEffect(() => {
    if (isInitialized) {
      startPollingForMessages({
        baseUrl: envState.base_url,
        userApiKey: authState.api_key,
        discourseUserId: authState.discourse_user_id
      });
    }
  }, [isInitialized]);
  /** End MessageBus routines */

  const setPassedReactRef = () => {
    const setRef = useCallback(n => {
      if (n) {
        props.passedReactRef.current = n;
        setReactRefReady(true);
      }
    }, []);
    return [setRef];
  };
  useEffect(() => {
    if (reactRefReady) {
      props.scrollToMe && props.scrollToMe();
    }
  }, [reactRefReady]);
  const renderItem = _ref => {
    let {
      item: comment
    } = _ref;
    return /*#__PURE__*/React.createElement(View, {
      my: 1,
      pt: 2,
      rounded: 'md',
      borderColor: colors.forumHomeScreen.header.border,
      borderWidth: 1
    }, /*#__PURE__*/React.createElement(Banner, {
      type: 'reply',
      refresh: refreshBannerForReplies,
      close: closeReplyBanner,
      topicIdList: topicIdListForReplies,
      topicId: comment.id
    }), /*#__PURE__*/React.createElement(Topic, {
      topic: comment,
      key: comment.id,
      updateTopicPost: updateTopicPost,
      updateTopicReplies: updateTopicReplies,
      showUserProfile: showUserProfile,
      setPassedReactRef: setPassedReactRef,
      forumKey: props.forumKey,
      trackForum: props.trackForum
    }));
  };
  return /*#__PURE__*/React.createElement(SafeAreaView, {
    style: {
      backgroundColor: colors.forum.safearea.bg,
      height: '80vh',
      maxHeight: '80vh',
      paddingTop: Platform.OS === 'android' ? StatusBar.currentHeight : 0
    }
  }, /*#__PURE__*/React.createElement(View, {
    flex: 1,
    bg: colors.forum.maincontainer.bg,
    onLayout: event => {
      var _event$nativeEvent, _event$nativeEvent$la;
      envDispatch({
        type: 'SET_LAYOUT_WIDTH',
        payload: event === null || event === void 0 ? void 0 : (_event$nativeEvent = event.nativeEvent) === null || _event$nativeEvent === void 0 ? void 0 : (_event$nativeEvent$la = _event$nativeEvent.layout) === null || _event$nativeEvent$la === void 0 ? void 0 : _event$nativeEvent$la.width
      });
    }
  }, /*#__PURE__*/React.createElement(Text, {
    fontSize: 'lg',
    fontWeight: "bold"
  }, "Leave comments..."), /*#__PURE__*/React.createElement(HandlePost, {
    platform: true,
    slug: slug,
    path: path,
    forceTypeTopic: true,
    updateTopicReplies: updateTopicReplies,
    showUserProfile: showUserProfile,
    trackForum: props.trackForum
  }), /*#__PURE__*/React.createElement(Banner, {
    type: 'topic',
    refresh: refreshBannerForTopics,
    close: closeReplyBanner,
    topicIdList: topicIdListForTopics,
    topicId: topicIdListForTopics[0]
  }), comments && comments.length ? /*#__PURE__*/React.createElement(FlatList, {
    data: comments,
    renderItem: renderItem,
    keyExtractor: comment => comment.id.toString(),
    onEndReached: getTopicsOfContentItem,
    onEndReachedThreshold: 0.2,
    w: '100%',
    h: "600px",
    maxH: "600px"
  }) : /*#__PURE__*/React.createElement(Center, null, /*#__PURE__*/React.createElement(Text, null, "Nothing here yet.")), showSpinner && /*#__PURE__*/React.createElement(View, {
    justifyContent: 'center',
    alignItems: 'center',
    w: '100%',
    h: "100px"
  }, /*#__PURE__*/React.createElement(Spinner, {
    mb: 3
  }), /*#__PURE__*/React.createElement(Text, null, "Loading Comments ..."))), profileUser && !props.toggleProfileModal && /*#__PURE__*/React.createElement(UserProfileModal, {
    profileUser: profileUser,
    setShowUserProfileModal: setShowUserProfileModal,
    showUserProfileModal: showUserProfileModal
  }));
};
export default Comments;
//# sourceMappingURL=Comments.js.map